import { Link } from "react-router-dom";
import {
  useGalleryUpload,
  type GalleryUploadJob,
} from "../../context/GalleryUploadContext";

function phaseLabel(job: GalleryUploadJob): string {
  if (job.kind === "download") {
    if (job.phase === "preparing") return "Preparing zip…";
    if (job.phase === "complete") return "Download ready";
    if (job.phase === "error") return "Download failed";
    return "Preparing zip…";
  }
  if (job.phase === "uploading") return "Uploading archive…";
  if (job.phase === "extracting") return "Extracting photos…";
  if (job.phase === "processing") return "Watermarking & compressing…";
  if (job.phase === "complete") return "Upload complete";
  return "Upload failed";
}

function progressPercent(job: GalleryUploadJob): number | null {
  if (job.kind === "download") return null;
  if (job.phase === "uploading") return job.uploadPercent ?? 0;
  if (job.phase === "processing" && job.total && job.total > 0) {
    return Math.round(((job.current ?? 0) / job.total) * 100);
  }
  return null;
}

function JobToast({ job }: { job: GalleryUploadJob }) {
  const { hideJob, removeJob, publishAlbum } = useGalleryUpload();
  const pct = progressPercent(job);
  const active =
    job.phase === "uploading" ||
    job.phase === "extracting" ||
    job.phase === "processing" ||
    job.phase === "preparing";
  const showIndeterminate = job.kind === "download" && job.phase === "preparing";

  return (
    <article
      className={`gallery-upload-toast${job.phase === "complete" ? " gallery-upload-toast--complete" : ""}${job.phase === "error" ? " gallery-upload-toast--error" : ""}`}
      aria-live="polite"
    >
      <header className="gallery-upload-toast-head">
        <div>
          <strong>{phaseLabel(job)}</strong>
          <span className="gallery-upload-toast-album">{job.albumTitle}</span>
          <span className="gallery-upload-toast-file">{job.fileName}</span>
        </div>
        <button
          type="button"
          className="gallery-upload-toast-close"
          aria-label="Hide progress"
          onClick={() => (job.phase === "complete" || job.phase === "error" ? removeJob(job.id) : hideJob(job.id))}
        >
          ×
        </button>
      </header>

      {active && pct != null && (
        <div className="admin-gallery-progress-bar-wrap">
          <div className="admin-gallery-progress-bar" style={{ width: `${pct}%` }} />
        </div>
      )}

      {showIndeterminate && (
        <div className="admin-gallery-progress-bar-wrap admin-gallery-progress-bar-wrap--indeterminate">
          <div className="admin-gallery-progress-bar admin-gallery-progress-bar--indeterminate" />
        </div>
      )}

      {job.kind === "upload" && job.phase === "extracting" && (
        <p className="gallery-upload-toast-detail">Reading images from zip…</p>
      )}

      {job.kind === "upload" && job.phase === "processing" && (
        <p className="gallery-upload-toast-detail">
          {job.current ?? 0} of {job.total ?? 0} photos
          {job.skipped ? ` · ${job.skipped} skipped` : ""}
          <span className="gallery-upload-toast-parallel"> · 3 at a time</span>
        </p>
      )}

      {job.kind === "download" && job.phase === "preparing" && (
        <p className="gallery-upload-toast-detail">
          Packaging watermarked photos — you can keep working in the admin.
        </p>
      )}

      {job.kind === "upload" && job.phase === "complete" && (
        <>
          <p className="gallery-upload-toast-detail">
            {job.photoCount} photo{job.photoCount === 1 ? "" : "s"} added
            {job.skipped ? ` (${job.skipped} skipped)` : ""}
          </p>
          {job.previewPhotos.length > 0 && (
            <div className="admin-gallery-upload-preview">
              {job.previewPhotos.map((p) => (
                <img key={p.id} src={p.thumbUrl ?? p.url} alt="" />
              ))}
            </div>
          )}
          <div className="gallery-upload-toast-actions">
            <a
              href={`/gallery/${job.albumSlug}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost btn-sm"
            >
              {job.albumPublished ? "View album" : "Preview draft album"}
            </a>
            <Link to="/admin/gallery" className="btn btn-ghost btn-sm">
              Open gallery admin
            </Link>
            {!job.albumPublished && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => void publishAlbum(job.id)}
              >
                Publish
              </button>
            )}
          </div>
        </>
      )}

      {job.kind === "download" && job.phase === "complete" && (
        <>
          <p className="gallery-upload-toast-detail">
            {job.downloadFilename ?? job.fileName} should be in your downloads folder.
          </p>
          {job.downloadUrl && (
            <div className="gallery-upload-toast-actions">
              <a
                href={job.downloadUrl}
                download={job.downloadFilename ?? job.fileName}
                className="btn btn-ghost btn-sm"
              >
                Save again
              </a>
            </div>
          )}
        </>
      )}

      {job.phase === "error" && (
        <p className="gallery-upload-toast-detail gallery-upload-toast-error">{job.error}</p>
      )}

      {active && (
        <p className="gallery-upload-toast-hint">
          You can close this panel or navigate away — it keeps running in the background.
        </p>
      )}
    </article>
  );
}

export function GalleryUploadToast() {
  const { jobs } = useGalleryUpload();
  const visible = jobs.filter((j) => !j.hidden);

  if (visible.length === 0) return null;

  return (
    <div className="gallery-upload-toast-stack" aria-label="Gallery background tasks">
      {visible.map((job) => (
        <JobToast key={job.id} job={job} />
      ))}
    </div>
  );
}
